import React, { useMemo, useState, useEffect } from "react";
import { auth, db } from "../../firebase/config";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import { Pie, Line } from "react-chartjs-2";
import "../../styles/Analytics.css";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// ✅ Register required chart types
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// --- Date range helpers (no extra libs) ---
const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);

function parseDateSafe(v) {
  // supports: Date, Firestore Timestamp-like {toDate()}, ISO string, or millis
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate();
  if (typeof v === "number") return new Date(v);
  if (typeof v === "string") {
    const dt = new Date(v);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

function inRangeInclusive(date, startISO, endISO) {
  const d = parseDateSafe(date);
  if (!d) return false;

  // normalize to date-only comparisons
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const start = new Date(startISO + "T00:00:00").getTime();
  const end = new Date(endISO + "T00:00:00").getTime();
  return day >= start && day <= end;
}

export default function EmployeeAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [companyId, setCompanyId] = useState(null);
  const [employees, setEmployees] = useState([]);

  // ✅ Date range state
  const [rangePreset, setRangePreset] = useState("LAST_30");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  // ✅ Initialize default range from preset
  // ✅ Initialize default range from preset (ESLint-safe)
  useEffect(() => {
    const today = new Date();
    const endISO = toISODate(today);

    // CUSTOM: only fill blanks, don't overwrite user values
    if (rangePreset === "CUSTOM") {
      if (!rangeStart) setRangeStart(endISO);
      if (!rangeEnd) setRangeEnd(endISO);
      return;
    }

    let start;
    if (rangePreset === "LAST_7") {
      start = new Date();
      start.setDate(today.getDate() - 6);
    } else if (rangePreset === "LAST_30") {
      start = new Date();
      start.setDate(today.getDate() - 29);
    } else if (rangePreset === "LAST_90") {
      start = new Date();
      start.setDate(today.getDate() - 89);
    } else if (rangePreset === "MTD") {
      start = startOfMonth(today);
    } else if (rangePreset === "YTD") {
      start = startOfYear(today);
    } else {
      start = new Date();
      start.setDate(today.getDate() - 29);
    }

    const startISO = toISODate(start);

    // ✅ only update if values actually changed (prevents loops)
    if (rangeStart !== startISO) setRangeStart(startISO);
    if (rangeEnd !== endISO) setRangeEnd(endISO);
  }, [rangePreset, rangeStart, rangeEnd]);
  // intentionally no rangeStart/rangeEnd deps

  // ✅ Guard: keep start <= end
  useEffect(() => {
    if (!rangeStart || !rangeEnd) return;
    const s = new Date(rangeStart + "T00:00:00").getTime();
    const e = new Date(rangeEnd + "T00:00:00").getTime();
    if (s > e) {
      // auto-fix by moving end to start
      setRangeEnd(rangeStart);
    }
  }, [rangeStart, rangeEnd]);

  // ✅ Get companyId from employer
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const employerDoc = await getDoc(doc(db, "users", user.uid));
          if (!employerDoc.exists()) throw new Error("User not found.");
          setCompanyId(employerDoc.data().companyId);
        } catch (err) {
          setError("Failed to fetch company ID.");
        }
      } else {
        setError("User not authenticated.");
      }
    });

    return () => unsubscribe();
  }, []);

  // ✅ Fetch employees once we have companyId
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!companyId) return;

      try {
        setLoading(true);
        const snapshot = await getDocs(
          collection(db, `companies/${companyId}/employees`)
        );
        const employeeList = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setEmployees(employeeList);
      } catch (err) {
        setError("Failed to fetch employees.");
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [companyId]);

  // ✅ Filtered employees for range-sensitive metrics (by hireDate)
  const employeesHiredInRange = useMemo(() => {
    if (!rangeStart || !rangeEnd) return employees;
    return employees.filter((emp) =>
      inRangeInclusive(emp.hireDate, rangeStart, rangeEnd)
    );
  }, [employees, rangeStart, rangeEnd]);

  // ✅ Guard rendering
  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!employees.length) return <p>No employee data found.</p>;

  // ✅ Analytics calculations (company-wide, not range-limited)
  const totalEmployees = employees.length;

  const roleCounts = employees.reduce((acc, emp) => {
    acc[emp.role] = (acc[emp.role] || 0) + 1;
    return acc;
  }, {});

  const statusCounts = employees.reduce((acc, emp) => {
    acc[emp.status] = (acc[emp.status] || 0) + 1;
    return acc;
  }, {});

  const genderCounts = employees.reduce((acc, emp) => {
    acc[emp.gender] = (acc[emp.gender] || 0) + 1;
    return acc;
  }, {});

  const departmentCounts = employees.reduce((acc, emp) => {
    acc[emp.group] = (acc[emp.group] || 0) + 1;
    return acc;
  }, {});

  const jobTitleCounts = employees.reduce((acc, emp) => {
    acc[emp.jobTitle] = (acc[emp.jobTitle] || 0) + 1;
    return acc;
  }, {});

  // ✅ Hiring Trends (range-limited)
  const hiringTrends = employeesHiredInRange.reduce((acc, emp) => {
    const d = parseDateSafe(emp.hireDate);
    if (!d) return acc;
    const month = d.toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
    acc[month] = (acc[month] || 0) + 1;
    return acc;
  }, {});

  // ✅ Tenure (choose: use all employees or range-hired employees)
  const tenureSource = employeesHiredInRange; // <- change to `employees` if you want tenure to ignore range

  const tenureGroups = {
    "<1 yr": 0,
    "1–3 yrs": 0,
    "3–5 yrs": 0,
    "5+ yrs": 0,
  };

  tenureSource.forEach((emp) => {
    const d = parseDateSafe(emp.hireDate);
    if (!d) return;

    const years = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365);
    if (years < 1) tenureGroups["<1 yr"]++;
    else if (years < 3) tenureGroups["1–3 yrs"]++;
    else if (years < 5) tenureGroups["3–5 yrs"]++;
    else tenureGroups["5+ yrs"]++;
  });

  // ✅ Age (company-wide; range usually doesn’t apply)
  const currentYear = new Date().getFullYear();
  const ageGroups = {
    "18–24": 0,
    "25–34": 0,
    "35–44": 0,
    "45–54": 0,
    "55+": 0,
  };

  employees.forEach((emp) => {
    const b = parseDateSafe(emp.birthDate);
    if (!b) return;

    const age = currentYear - b.getFullYear();
    if (age < 25) ageGroups["18–24"]++;
    else if (age < 35) ageGroups["25–34"]++;
    else if (age < 45) ageGroups["35–44"]++;
    else if (age < 55) ageGroups["45–54"]++;
    else ageGroups["55+"]++;
  });

  const formatMDY = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    if (!y || !m || !d) return iso;
    return `${m}/${d}/${y}`;
  };

  // ✅ Chart data
  const statusData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        data: Object.values(statusCounts),
        backgroundColor: ["#2DB882", "#FFBB28", "#FF8042"],
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  };

  const trendLabels = Object.keys(hiringTrends).sort();
  const trendValues = trendLabels.map((label) => hiringTrends[label]);

  const hiringTrendData = {
    labels: trendLabels,
    datasets: [
      {
        label: `New Hires (${formatMDY(rangeStart)} → ${formatMDY(rangeEnd)})`,

        data: trendValues,
        fill: true,
        borderColor: "#007bff",
        backgroundColor: "rgba(0, 123, 255, 0.1)",
        tension: 0.4,
        pointBackgroundColor: "#007bff",
      },
    ],
  };

  const genderData = {
    labels: Object.keys(genderCounts),
    datasets: [
      {
        data: Object.values(genderCounts),
        backgroundColor: ["#36A2EB", "#FF6384", "#FFCE56"],
      },
    ],
  };

  const deptData = {
    labels: Object.keys(departmentCounts),
    datasets: [
      {
        data: Object.values(departmentCounts),
        backgroundColor: ["#4BC0C0", "#9966FF", "#FF9F40"],
      },
    ],
  };

  const ageData = {
    labels: Object.keys(ageGroups),
    datasets: [
      {
        label: "Employees",
        data: Object.values(ageGroups),
        backgroundColor: "#FF6384",
      },
    ],
  };

  const tenureData = {
    labels: Object.keys(tenureGroups),
    datasets: [
      {
        label: `Employees (hired in range)`,
        data: Object.values(tenureGroups),
        backgroundColor: "#36A2EB",
      },
    ],
  };

  const jobTitleData = {
    labels: Object.keys(jobTitleCounts),
    datasets: [
      {
        label: "Employees",
        data: Object.values(jobTitleCounts),
        backgroundColor: "#FFCE56",
      },
    ],
  };

  // ✅ Chart options
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { font: { size: 14 } },
      },
    },
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        labels: { font: { size: 14 } },
      },
    },
  };

  return (
    <div className="management-analytics-wrapper">
      {/* ✅ Date range controls */}
      <div className="analytics-rangebar">
        <div className="analytics-filter">
          <label className="analytics-filter-label">Date Range</label>
          <select
            className="analytics-dropdown"
            value={rangePreset}
            onChange={(e) => setRangePreset(e.target.value)}
          >
            <option value="LAST_7">Last 7 days</option>
            <option value="LAST_30">Last 30 days</option>
            <option value="LAST_90">Last 90 days</option>
            <option value="MTD">Month to date</option>
            <option value="YTD">Year to date</option>
            <option value="CUSTOM">Custom</option>
          </select>
        </div>

        <div className="analytics-filter">
          <label className="analytics-filter-label">Start</label>
          <input
            className="analytics-date-input"
            type="date"
            value={rangeStart}
            onChange={(e) => {
              setRangePreset("CUSTOM");
              setRangeStart(e.target.value);
            }}
          />
        </div>

        <div className="analytics-filter">
          <label className="analytics-filter-label">End</label>
          <input
            className="analytics-date-input"
            type="date"
            value={rangeEnd}
            onChange={(e) => {
              setRangePreset("CUSTOM");
              setRangeEnd(e.target.value);
            }}
          />
        </div>

        <div className="analytics-range-pill">
          {rangeStart && rangeEnd
            ? `${formatMDY(rangeStart)} → ${formatMDY(rangeEnd)}`
            : "—"}
        </div>
      </div>

      {/* KPI grid */}
      <div className="kpi-grid kpi-grid--employees">
        <div className="kpi-card accent-green">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Total Employees</span>
          </div>
          <div className="kpi-value">{totalEmployees}</div>
          <div className="kpi-sub">&nbsp;</div>
        </div>

        <div className="kpi-card accent-indigo">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Roles</span>
          </div>
          <div className="kpi-value">
            {(roleCounts["employee"] || 0) + (roleCounts["manager"] || 0)}
          </div>
          <div className="kpi-sub">
            Emp: <strong>{roleCounts["employee"] || 0}</strong> &nbsp;·&nbsp;
            Mgr: <strong>{roleCounts["manager"] || 0}</strong>
          </div>
        </div>

        <div className="kpi-card accent-blue">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Status</span>
          </div>
          <div className="kpi-mini">
            <Pie data={statusData} options={pieOptions} />
          </div>
          <div className="kpi-sub">
            Active:{" "}
            <strong>
              {statusCounts["active"] || statusCounts["Active"] || 0}
            </strong>
            {"  "} · {"  "}
            Other:{" "}
            <strong>
              {totalEmployees -
                ((statusCounts["active"] || 0) + (statusCounts["Active"] || 0))}
            </strong>
          </div>
        </div>

        <div className="kpi-card accent-amber">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Departments</span>
          </div>
          <div className="kpi-value">
            {Object.keys(departmentCounts).length}
          </div>
          <div className="kpi-sub">
            {Object.keys(genderCounts)
              .slice(0, 3)
              .map((g, i) => (
                <span key={g}>
                  {i > 0 ? " · " : ""}
                  {g}: <strong>{genderCounts[g]}</strong>
                </span>
              ))}
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        <div className="chart-container">
          <h3>Hiring Trends</h3>
          <Line data={hiringTrendData} options={lineOptions} />
        </div>

        <div className="chart-container pie-chart">
          <h3>Gender Distribution</h3>
          <Pie data={genderData} options={pieOptions} />
        </div>

        <div className="chart-container pie-chart">
          <h3>Department Breakdown</h3>
          <Pie data={deptData} options={pieOptions} />
        </div>

        <div className="chart-container">
          <h3>Age Distribution</h3>
          <Line data={ageData} options={lineOptions} />
        </div>

        <div className="chart-container">
          <h3>Tenure Groups</h3>
          <Line data={tenureData} options={lineOptions} />
        </div>

        <div className="chart-container">
          <h3>Job Title Breakdown</h3>
          <Line data={jobTitleData} options={lineOptions} />
        </div>
      </div>
    </div>
  );
}
